import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { Account } from '@/api/commercial/accounts';
import { type CreditAnalysis, creditAnalysisApi } from '@/api/commercial/credit-analysis';
import {
  type AccountType,
  accountTypeLabels,
  complementaryDocKeys,
  fieldsByAccountType,
  partnerDirectorDocKey,
} from '@/hooks/commercial/use-credit-analysis-state-machine';
import { useToast } from '@/hooks/use-toast';
import { fetchWithAuth } from '@/lib/api';

export interface PartnerDirector {
  id: string;
  name: string;
  cpf: string;
  document: File | null;
}

export interface UploadedFileInfo {
  file: File;
  key: string;
  eTag?: string;
}

export interface UseDocumentUploadFormParams {
  proposalId: string;
  account: Account | null;
  accountType: AccountType;
  creditAnalysis: CreditAnalysis | null;
  onSuccess?: (creditAnalysis: CreditAnalysis) => void;
}

const getFileExtension = (fileName: string) => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop() : '';
};

export function useDocumentUploadForm({
  proposalId,
  account,
  accountType,
  creditAnalysis,
  onSuccess,
}: UseDocumentUploadFormParams) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Files state
  const [files, setFiles] = useState<Record<string, File | null>>({});

  // Upload progress and results
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadedFileInfo>>({});
  const [isUploading, setIsUploading] = useState(false);

  // Partners/Directors state (for ME)
  const [partnersDirectors, setPartnersDirectors] = useState<PartnerDirector[]>([
    { id: `partner-${Date.now()}`, name: '', cpf: '', document: null },
  ]);

  // Determine effective account type based on document
  const derivedAccountType: AccountType = useMemo(() => {
    if (account?.document?.type === 'cpf') return 'PF';
    if (account?.document?.type === 'cnpj') return 'ME';
    return accountType;
  }, [account, accountType]);

  // Get current fields (excluding partner/director and complementary docs)
  const currentFields = useMemo(
    () =>
      fieldsByAccountType[derivedAccountType].filter(
        (field) =>
          field.key !== partnerDirectorDocKey && !complementaryDocKeys.includes(field.key as any),
      ),
    [derivedAccountType],
  );

  // Get complementary fields (for ME)
  const complementaryFields = useMemo(
    () =>
      derivedAccountType === 'ME'
        ? fieldsByAccountType[derivedAccountType].filter((field) =>
            complementaryDocKeys.includes(field.key as any),
          )
        : [],
    [derivedAccountType],
  );

  // Combined mutation for uploading all documents
  const uploadDocumentsMutation = useMutation({
    mutationFn: async () => {
      if (!account?.id) {
        throw new Error('Account ID is required');
      }

      // Collect all files to upload
      const filesToUpload: Array<{
        fieldId: string;
        file: File;
        fileName: string;
        contentType: string;
      }> = [];

      // Add main documents
      Object.entries(files).forEach(([key, file]) => {
        if (file) {
          const field =
            currentFields.find((f) => f.key === key) ||
            complementaryFields.find((f) => f.key === key);
          if (field) {
            filesToUpload.push({
              fieldId: key,
              file,
              fileName: file.name,
              contentType: file.type || 'application/octet-stream',
            });
          }
        }
      });

      // Add partner/director documents
      partnersDirectors.forEach((partner) => {
        if (partner.document) {
          filesToUpload.push({
            fieldId: `partner-doc-${partner.id}`,
            file: partner.document,
            fileName: partner.document.name,
            contentType: partner.document.type || 'application/octet-stream',
          });
        }
      });

      // Step 1: Generate signed URLs for all files
      const uploadPromises = filesToUpload.map(async (fileInfo) => {
        setUploadProgress((prev) => ({ ...prev, [fileInfo.fieldId]: 10 }));

        // Get signed URL from S3
        const s3Key = `${account.id}/uploads/credit-analysis/${fileInfo.fieldId}.${getFileExtension(fileInfo.fileName)}`;

        const response = await fetchWithAuth('/api/upload/s3/sign', {
          method: 'POST',
          body: JSON.stringify({
            key: s3Key,
            contentType: fileInfo.contentType,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to get signed URL for ${fileInfo.fileName}`);
        }

        const result = await response.json();
        const { uploadUrl, key } = result.data || result;

        setUploadProgress((prev) => ({ ...prev, [fileInfo.fieldId]: 30 }));

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: fileInfo.file,
          headers: { 'Content-Type': fileInfo.contentType },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${fileInfo.fileName} to S3`);
        }

        const eTag = uploadResponse.headers.get('ETag') || undefined;

        setUploadProgress((prev) => ({ ...prev, [fileInfo.fieldId]: 70 }));

        return {
          fieldId: fileInfo.fieldId,
          fileName: fileInfo.fileName,
          key,
          sizeInBytes: fileInfo.file.size,
          contentType: fileInfo.contentType,
          eTag,
        };
      });

      const uploadedResults = await Promise.all(uploadPromises);

      // Step 2: Persist metadata and create/update credit analysis
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        Object.keys(newProgress).forEach((key) => {
          newProgress[key] = 90;
        });
        return newProgress;
      });

      const result = await creditAnalysisApi.uploadDocuments({
        accountId: account.id,
        proposalId,
        creditAnalysisId: creditAnalysis?.id,
        files: uploadedResults,
      });

      // Mark all as uploaded
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        Object.keys(newProgress).forEach((key) => {
          newProgress[key] = 100;
        });
        return newProgress;
      });

      return result;
    },
    onSuccess: (updatedCreditAnalysis) => {
      toast({
        title: 'Documentos enviados',
        description: 'Os documentos foram enviados com sucesso para análise!',
      });

      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: ['creditAnalysis'],
      });
      queryClient.invalidateQueries({
        queryKey: ['creditAnalysis', 'proposal', proposalId],
      });
      queryClient.invalidateQueries({
        queryKey: ['creditAnalysis', 'account', account?.id],
      });
      queryClient.invalidateQueries({
        queryKey: ['creditAnalysisTimeline', updatedCreditAnalysis.id],
      });

      // Reset state
      setFiles({});
      setPartnersDirectors([{ id: `partner-${Date.now()}`, name: '', cpf: '', document: null }]);
      setUploadProgress({});
      setUploadedFiles({});

      // Call success callback
      onSuccess?.(updatedCreditAnalysis);
    },
    onError: (error) => {
      console.error('Error uploading documents:', error);
      toast({
        title: 'Erro',
        description:
          error instanceof Error
            ? error.message
            : 'Ocorreu um erro ao enviar os documentos. Tente novamente.',
        variant: 'destructive',
      });
      setIsUploading(false);
      setUploadProgress({});
    },
  });

  const handleFileChange = useCallback((key: string, file: File | null) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
  }, []);

  const handlePartnerDirectorChange = useCallback(
    (id: string, field: keyof PartnerDirector, value: string | File | null) => {
      setPartnersDirectors((prev) =>
        prev.map((pd) => (pd.id === id ? { ...pd, [field]: value } : pd)),
      );
    },
    [],
  );

  const addPartnerDirector = useCallback(() => {
    setPartnersDirectors((prev) => [
      ...prev,
      { id: `partner-${Date.now()}`, name: '', cpf: '', document: null },
    ]);
  }, []);

  const removePartnerDirector = useCallback((id: string) => {
    setPartnersDirectors((prev) => prev.filter((pd) => pd.id !== id));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if at least one file is selected
    const hasFiles = Object.values(files).some((f) => f !== null);
    const hasPartnerDocs = partnersDirectors.some((p) => p.document !== null);

    if (!hasFiles && !hasPartnerDocs) {
      toast({
        title: 'Nenhum arquivo selecionado',
        description: 'Selecione pelo menos um documento para enviar.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    try {
      await uploadDocumentsMutation.mutateAsync();
    } finally {
      setIsUploading(false);
    }
  };

  return {
    // State
    files,
    uploadProgress,
    uploadedFiles,
    isUploading,
    partnersDirectors,
    derivedAccountType,
    currentFields,
    complementaryFields,
    accountTypeLabels,
    // Handlers
    handleFileChange,
    handlePartnerDirectorChange,
    addPartnerDirector,
    removePartnerDirector,
    handleSubmit,
    // Utilities
    getFileExtension,
  };
}
